import React from "react";
import logo from "../assets/img/om-full-primary-logo.svg";
import signature from "../assets/img/signature.jpeg";
import { getUserDetail } from "../util/util";

export const TransactionStatement = ({ 
  companyName = '', 
  rows = [], 
  columns = [], 
  companyId, 
  signatureUrl = '/signature.png',
  firstPageSize = 21,
  pageSize = 24
}) => {
  const user = typeof window !== 'undefined' ? getUserDetail?.() : null;

  const formatPersonName = (rawName) => {
    if (!rawName || typeof rawName !== 'string') return '';
    const formatSegment = (segment = '') => segment
      .split(/([-'])/)
      .map((part) => (part === '-' || part === "'") ? part : part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join('');
    return rawName
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .map(formatSegment)
      .join(' ');
  };

  const derivedName = user?.full_name || user?.name || companyName || 'Valued Customer';
  const recipientName = formatPersonName(derivedName) || 'Valued Customer';

  // Pagination logic: configurable rows per page
  function paginateRows(rows, fpSize, pSize) {
    if (rows.length <= fpSize) return [rows];
    const pages = [];
    pages.push(rows.slice(0, fpSize));
    let i = fpSize;
    while (i < rows.length) {
      pages.push(rows.slice(i, i + pSize));
      i += pSize;
    }
    return pages;
  }
  const pagedRows = paginateRows(rows, firstPageSize, pageSize);

  // Totals and period
  const totalCredits = rows.reduce((s, r) => s + (r.amount > 0 ? r.amount : 0), 0);
  const totalDebits = rows.reduce((s, r) => s + (r.amount < 0 ? Math.abs(r.amount) : 0), 0);
  const dates = rows.map(r => new Date(r.date)).filter(d => !isNaN(d.getTime()));
  const period = dates.length ? `${dates.reduce((a,b)=> a<b?a:b).toLocaleDateString('en-US',{ month: 'long', year: 'numeric' })} - ${dates.reduce((a,b)=> a>b?a:b).toLocaleDateString('en-US',{ month: 'long', year: 'numeric' })}` : '';

  const fmt = (v) => `GHS ${Math.abs(Number(v)).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

  // Calculate dynamic column widths based on content for ALL columns
  const getDynamicColumnWidths = () => {
    // Calculate required width for each column based on content length
    const colWidths = columns.map((col) => {
      // Get header length
      const headerLength = col.title ? String(col.title).length : 0;
      
      // Find max content length in this column
      let maxContentLength = 0;
      rows.forEach(row => {
        let value = '';
        if (col.render) {
          // For rendered columns, estimate based on raw data
          value = String(row[col.dataIndex] || '');
        } else {
          value = String(row[col.dataIndex] || '');
        }
        if (value.length > maxContentLength) {
          maxContentLength = value.length;
        }
      });
      
      // Use the longer of header or content
      const maxLength = Math.max(headerLength, maxContentLength);
      
      // Calculate width: base 8 chars = 10%, each extra char adds 0.8%
      // Min 8%, max 35% per column
      let widthPercent = Math.max(8, Math.min(35, 10 + (maxLength * 0.8)));
      
      // Special handling for specific column types
      const colKey = col.key || col.dataIndex || '';
      if (colKey === 'type' || colKey === 'transaction' || colKey === 'pension_type_name') {
        widthPercent = Math.max(widthPercent, 15); // Minimum 15% for type columns
        return { width: widthPercent, nowrap: true };
      }
      if (colKey === 'amount' || colKey === 'received' || colKey === 'net_premium') {
        widthPercent = Math.max(widthPercent, 12); // Minimum 12% for amount
        return { width: widthPercent, nowrap: true };
      }
      if (colKey === 'date' || colKey === 'payment_date') {
        widthPercent = Math.max(widthPercent, 11); // Minimum 11% for date
        return { width: widthPercent, nowrap: true };
      }
      if (colKey === 'reference' || colKey === 'receipt_number') {
        widthPercent = Math.min(widthPercent, 30); // Cap reference at 30%
        return { width: widthPercent, breakAll: true };
      }
      
      return { width: widthPercent };
    });
    
    // Normalize widths to total 100%
    const totalWidth = colWidths.reduce((sum, col) => sum + col.width, 0);
    const scaleFactor = 100 / totalWidth;
    
    return colWidths.map((col, idx) => {
      const isLast = idx === columns.length - 1;
      const adjustedWidth = Math.max(6, col.width * scaleFactor); // Min 6% per column
      return {
        width: `${adjustedWidth}%`,
        borderRight: isLast ? 'none' : '1px solid #888',
        textAlign: 'center',
        whiteSpace: col.nowrap ? 'nowrap' : 'normal',
        wordBreak: col.breakAll ? 'break-all' : 'normal',
        fontSize: col.breakAll ? '8px' : '9px'
      };
    });
  };
  
  const dynamicWidths = getDynamicColumnWidths();

  const headerCellBaseStyle = {
    textAlign: 'center',
    padding: '4px 3px',
    color: '#222',
    borderRight: '1px solid #888',
    fontSize: '9px'
  };

  const dataCellBaseStyle = {
    padding: '3px 3px',
    color: '#222',
    fontWeight: 500,
    borderRight: '1px solid #888',
    fontSize: '8px',
    verticalAlign: 'middle'
  };

  const montserratFontCSS = `
    @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;900&display=swap');
    .pdf-page, .pdf-page * {
      font-family: 'Montserrat', 'Helvetica Neue', Helvetica, Arial, sans-serif !important;
      letter-spacing: 0.02em;
    }
    .pdf-table {
      table-layout: fixed;
      width: 100%;
      max-width: 100%;
      font-size: 9px;
    }
    .pdf-table th,
    .pdf-table td {
      word-break: break-word;
      word-wrap: break-word;
      overflow-wrap: break-word;
      white-space: normal;
      max-width: 100%;
    }
    /* Transaction Type/Pension Type column - ellipsis for overflow */
    .pdf-table th.type-col,
    .pdf-table td.type-col,
    .pdf-table th[data-col="type"],
    .pdf-table td[data-col="type"],
    .pdf-table th[data-col="transaction"],
    .pdf-table td[data-col="transaction"],
    .pdf-table th[data-col="pension_type_name"],
    .pdf-table td[data-col="pension_type_name"],
    .pdf-table th:nth-child(2),
    .pdf-table td:nth-child(2) {
      white-space: nowrap !important;
      overflow: hidden !important;
      text-overflow: ellipsis !important;
      line-height: 1.4 !important;
      padding-top: 6px !important;
      padding-bottom: 6px !important;
    }
    /* Scheme Name column - allow wrapping since it has more width */
    .pdf-table th[data-col="scheme_name"],
    .pdf-table td[data-col="scheme_name"] {
      white-space: normal !important;
      word-break: normal !important;
      overflow-wrap: normal !important;
      line-height: 1.2 !important;
      padding-top: 3px !important;
      padding-bottom: 3px !important;
    }
    .pdf-table th {
      vertical-align: middle;
      line-height: 1.2;
      font-size: 10px !important;
    }
    .pdf-table td {
      vertical-align: middle;
      line-height: 1.3;
      font-size: 9px !important;
    }
    .pdf-bold-emphasis {
      font-weight: 700 !important;
      font-size: 16px !important;
      line-height: 30px !important;
      letter-spacing: -1px !important;
      vertical-align: middle;
    }
    .pdf-title-underline {
      font-weight: 700 !important;
      font-size: 16px !important;
      line-height: 30px !important;
      letter-spacing: -1px !important;
      vertical-align: middle;
      text-decoration: underline solid currentColor;
      text-underline-offset: 0;
      text-decoration-thickness: from-font;
    }
    /* Hide table and summary on mobile screens, show message instead */
    @media screen and (max-width: 768px) {
      .pdf-table-container,
      .pdf-summary-container {
        display: none !important;
      }
      .mobile-table-message {
        display: block !important;
        padding: 24px 16px;
        background: #f5f5f5;
        border-radius: 8px;
        text-align: center;
        margin: 16px 0;
      }
      .mobile-table-message i {
        font-size: 48px;
        color: #666;
        margin-bottom: 12px;
      }
      .mobile-table-message p {
        margin: 0;
        color: #666;
        font-size: 14px;
      }
    }
    @media screen and (min-width: 769px) {
      .mobile-table-message {
        display: none !important;
      }
    }
    /* Always show table and summary for print/PDF */
    @media print {
      .pdf-table-container,
      .pdf-summary-container {
        display: block !important;
      }
      .mobile-table-message {
        display: none !important;
      }
    }
    /* Force show for PDF generation (used by js) */
    .force-pdf-visible .pdf-table-container,
    .force-pdf-visible .pdf-summary-container {
      display: block !important;
    }
    .force-pdf-visible .mobile-table-message {
      display: none !important;
    }
  `;

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <style dangerouslySetInnerHTML={{ __html: montserratFontCSS }} />
      {/* Inline styles to help html2pdf/jsPDF paginate to A4 and avoid overlapping */}
      <style dangerouslySetInnerHTML={{__html: `
        @media print {
          .pdf-page { width: 100%; box-sizing: border-box; }
          .signature-section { page-break-inside: avoid; break-inside: avoid; -webkit-column-break-inside: avoid; }
          table { page-break-inside: auto; }
          thead { display: table-header-group; }
          tr { page-break-inside: avoid; break-inside: avoid; }
          /* Only page break if signature doesn't fit after table */
          .signature-section {
            page-break-before: auto;
            orphans: 3;
            widows: 3;
          }
        }
        /* PDF table sizing - ensure it fits within A4 width */
        .pdf-page {
          max-width: 190mm !important;
          margin: 0 auto !important;
          padding: 8mm !important;
        }
        .pdf-table { 
          table-layout: fixed !important; 
          width: 100% !important; 
          max-width: 100% !important;
          font-size: 8px !important;
        }
        .pdf-table th, .pdf-table td { 
          padding: 3px 2px !important; 
          font-size: 8px !important;
          line-height: 1.1 !important;
          word-break: break-word !important;
          word-wrap: break-word !important;
          overflow-wrap: break-word !important;
          white-space: normal !important;
          vertical-align: middle !important;
          max-width: 100% !important;
          box-sizing: border-box !important;
          hyphens: auto !important;
        }
        .pdf-table th { 
          font-size: 9px !important;
          vertical-align: middle !important; 
          line-height: 1.1 !important; 
        }
        /* Ensure column widths are respected */
        .pdf-table th:last-child,
        .pdf-table td:last-child {
          border-right: none !important;
        }
        /* Pension Type column - ellipsis for overflow */
        .pdf-table th.type-col,
        .pdf-table td.type-col,
        .pdf-table th[data-col="type"],
        .pdf-table td[data-col="type"],
        .pdf-table th[data-col="transaction"],
        .pdf-table td[data-col="transaction"],
        .pdf-table th[data-col="pension_type_name"],
        .pdf-table td[data-col="pension_type_name"] {
          line-height: 1.4 !important;
          padding-top: 6px !important;
          padding-bottom: 6px !important;
          white-space: nowrap !important;
          overflow: hidden !important;
          text-overflow: ellipsis !important;
          max-width: 100% !important;
        }
        /* Scheme Name column - allow wrapping since it has more width */
        .pdf-table th[data-col="scheme_name"],
        .pdf-table td[data-col="scheme_name"] {
          white-space: normal !important;
          word-break: normal !important;
          overflow-wrap: normal !important;
          line-height: 1.2 !important;
          padding-top: 3px !important;
          padding-bottom: 3px !important;
        }
      `}} />
  {/* Document Container (one printable PDF page block; will allow automatic breaks) */}
  <div className="bg-white shadow-md max-w-4xl mx-auto pdf-page" style={{ padding: 0 }}>
        {/* Top Green Border */}
        <div className="h-2 bg-green-600 w-full"></div>

        {/* Document Content - reduced padding for PDF fit */}
        <div className="p-4">
       
          <div
            style={{
              borderTop: "8px solid #008000",
              borderBottom: "8px solid #008000",
              padding: "10px 0 10px 0",
              textAlign: "left",
              marginBottom: 24
            }}
          >
            <img src={logo} alt="Old Mutual Logo" style={{ height: "40px" }} />
          </div>
          <div className="text-sm text-gray-500" style={{ marginBottom: 20 }}>{new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</div>
          <div className="mb-6" style={{ marginBottom: 24 }}>
            <div className="text-sm">Dear {recipientName},</div>
          </div>
          {/* Title */}
          <div className="mb-6">
            <div className="pdf-title-underline" style={{ color: '#222' }}>TRANSACTION STATEMENT</div>
      <div className="text-sm" style={{ marginBottom: 28 }}>Kindly find below your transaction Statement</div>
          </div>

          {/* Summary Section */}
          <div className="mb-6 pdf-summary-container">
            <div className="pdf-bold-emphasis" style={{ color: '#222', marginBottom: 8 }}>SUMMARY</div>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
                <span className="text-gray-600" style={{ minWidth: 110 }}>Currency:</span>
                <span style={{ fontWeight: 600, color: '#222' }}>GHS</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
                <span className="text-gray-600" style={{ minWidth: 110 }}>Total Credits:</span>
                <span style={{ fontWeight: 600, color: '#222' }}>{fmt(totalCredits)}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
                <span className="text-gray-600" style={{ minWidth: 110 }}>Total Debits:</span>
                <span style={{ fontWeight: 600, color: '#222' }}>{fmt(totalDebits)}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 24, marginBottom: 28 }}>
                <span className="text-gray-600" style={{ minWidth: 110 }}>Period:</span>
                <span style={{ fontWeight: 600, color: '#222' }}>{period || 'N/A'}</span>
              </div>
            </div>
          </div>

          {/* Mobile Message - shown on small screens */}
          <div className="mobile-table-message" style={{ display: 'none' }}>
            <i className="bi bi-file-earmark-pdf"></i>
            <p><strong>Statement preview not available on mobile</strong></p>
            <p>Please download the PDF to view the full transaction statement with summary and table.</p>
          </div>

          {/* Statement Table */}
          <div className="pdf-table-container">
            {pagedRows.map((page, pageIdx) => (
              <div
                className="mb-4"
                key={pageIdx}
                style={{
                  pageBreakInside: 'auto',
                  breakInside: 'auto',
                  marginTop: pageIdx === 0 ? undefined : 24
                }}
              >
                {pageIdx === 0 && <div className="pdf-bold-emphasis" style={{ color: '#222', marginBottom: 8, marginTop: 0 }}>STATEMENT</div>}
                <table className="pdf-table" style={{ borderCollapse: 'collapse', fontSize: 15, background: '#fff', border: '1px solid #888' }}>
                  <thead>
                    <tr style={{ background: '#fff', borderBottom: '1px solid #888' }}>
                      {columns.map((col, idx) => (
                        <th key={col.key || idx} 
                            className={`pdf-bold-emphasis ${col.dataIndex === 'type' || col.dataIndex === 'transaction' || col.dataIndex === 'pension_type_name' ? 'type-col' : ''}`} 
                            data-col={col.dataIndex}
                            style={{ ...headerCellBaseStyle, ...(col.widthStyle || dynamicWidths[idx]) }}>
                          {col.title}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {page.map((transaction, index) => (
                      <tr key={index + pageIdx * 21} style={{ borderBottom: '1px solid #888' }}>
                        {columns.map((col, idx) => (
                          <td key={col.key || idx} 
                              className={col.dataIndex === 'type' || col.dataIndex === 'transaction' || col.dataIndex === 'pension_type_name' ? 'type-col' : ''}
                              data-col={col.dataIndex}
                              style={{ ...dataCellBaseStyle, ...(col.widthStyle || dynamicWidths[idx]) }}>
                            {col.render ? col.render(transaction[col.dataIndex], transaction) : transaction[col.dataIndex]}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
          
          {/* Footer Messages and Signature - Flows naturally after table */}
          <div className="signature-section" style={{ marginTop: 8, pageBreakInside: 'auto', breakInside: 'auto' }}>
            <div className="text-sm mb-6">
              <div className="mb-2">Please do not indicate to insert if further clarification is required.</div>
              <div className="mb-2">We have no benefits from you shortly.</div>
              <div>Yours faithfully.</div>
            </div>

            {/* Bottom Separator */}
            <div className="h-px bg-gray-300 my-4"></div>

            {/* Signature block - show image if available, otherwise show dotted line */}
            <div style={{ marginTop: 8, marginBottom: 8 }}>
              {signature  ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                  <img src={signature } alt="signature" style={{ height: 80, objectFit: 'contain', marginBottom: 6 }} />
                   <div style={{ fontSize: 20, color: '#999', marginBottom: 0 }}>..................................................</div>
                </div>
                
              ) : (
                <div style={{ fontSize: 20, color: '#999', marginBottom: 6 }}>..................................................</div>
              )}

            {/* Footer View Note */}
            <div className="text-sm text-gray-500 mb-1">Wisdom Kwabena Donor</div>
            <div className="text-sm text-gray-500">(Head, Corporate Distribution)</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TransactionStatement;